import { useEffect, useState } from 'react';
import { Box, Divider, Grid, MenuItem, TextField } from '@mui/material';

import { fetchFiles } from '../../../services/syntiro_honey/data_ingestion.service';
import { FileContext, FileListItem } from '../../../types/syntiro_honey';
import { CaptionText } from '../../shared/typography/PrimaryTypography';
import MetricHintsField from './MetricHintsField';

/**
 * Editable form state behind a `FileContext`. Kept as plain strings so the
 * inputs stay fully controlled — except the metric hints, which stay an array
 * end to end (the wire shape) so a hint containing a comma survives intact.
 * Converted to the wire shape on submit via `toFileContext`.
 */
export interface FileContextFormValue {
  /** Free-text entity hint (e.g. "Cape Town site"). */
  entityHint: string;
  /** Free-text reporting-period hint (e.g. "FY26"). */
  periodHint: string;
  /** Free-text unit hint (e.g. "MWh"). */
  unitHint: string;
  /** Metric hints, one per entry (e.g. ["Scope 1", "Water"]). */
  metricHints: Array<string>;
  /** A free-text sentence of context fed into the AI extraction. */
  contextNote: string;
  /** Id of an earlier processed file this one replaces ('' = a new file). */
  supersedesFileId: string;
}

/** A blank context form value (all hints empty). */
export const EMPTY_FILE_CONTEXT_FORM_VALUE: FileContextFormValue = {
  entityHint: '',
  periodHint: '',
  unitHint: '',
  metricHints: [],
  contextNote: '',
  supersedesFileId: ''
};

/**
 * Convert a form value to the wire `FileContext`, dropping blank fields.
 * @param {FileContextFormValue} value The form state to convert.
 * @returns {FileContext | undefined} The context, or undefined when every field is blank.
 */
export const toFileContext = (value: FileContextFormValue): FileContext | undefined => {
  const context: FileContext = {};
  if (value.entityHint.trim() !== '') {
    context.entityHint = value.entityHint.trim();
  }
  if (value.periodHint.trim() !== '') {
    context.periodHint = value.periodHint.trim();
  }
  if (value.unitHint.trim() !== '') {
    context.unitHint = value.unitHint.trim();
  }
  if (value.metricHints.length > 0) {
    context.metricHints = value.metricHints;
  }
  if (value.contextNote.trim() !== '') {
    context.contextNote = value.contextNote.trim();
  }
  if (value.supersedesFileId !== '') {
    context.supersedesFileId = value.supersedesFileId;
  }
  return Object.keys(context).length > 0 ? context : undefined;
};

/**
 * Examples shown in the (now permanently visible) placeholders.
 *
 * Three examples per field, not one: a single example teaches a FORMAT, and the
 * uploader's problem is not knowing the RANGE — that "Period" accepts a whole
 * sentence, or that "Unit" accepts a per-metric breakdown, is invisible from
 * "e.g. FY26" and "e.g. MWh".
 */
const PLACEHOLDER = {
  // Deliberately generic: the earlier example quoted a real Pick n Pay store code,
  // which showed one client's internal coding to every other client (Maxine ①).
  entity: 'Cape Town Site · Coastal Division · South Africa',
  period: 'FY2026 · Months are all in 2026 · Jan–Dec 2025',
  unit: 'MWh · Solar in kWh, water in kl',
  metrics: 'Scope 1 · Water withdrawal · Electricity purchased',
  note: 'Sheet 3 is last year — ignore it. The totals row is already summed.'
} as const;

interface HintGroupProps {
  /** The group name, e.g. "Where and when". */
  name: string;
  /** What the group's hints drive, e.g. "guides the AI". */
  what: string;
  /** Draw a divider above the heading (every group after the first). */
  divided?: boolean;
}

/**
 * A labelled heading over a pair of hint fields. The two groups mirror where the
 * hints are actually consumed — see {@link FileContextForm} — so the grouping is a
 * fact about the pipeline rather than a tidy-looking arrangement.
 * @param {string} name The group name.
 * @param {string} what What the group's hints drive.
 * @param {boolean | undefined} divided Draw a divider above the heading.
 * @returns {JSX.Element}
 */
function HintGroup({ name, what, divided }: HintGroupProps) {
  return (
    <Grid size={{ xs: 12 }}>
      {divided === true && <Divider sx={{ mb: 2 }} />}
      <CaptionText weight="Medium" sx={{ display: 'block' }}>
        {name}
        <Box component="span" sx={{ color: 'text.secondary', fontWeight: 400 }}>
          {` — ${what}`}
        </Box>
      </CaptionText>
    </Grid>
  );
}

/**
 * Shrinks every label permanently, so the placeholder is visible before the field
 * is focused.
 *
 * MUI renders a `placeholder` at `opacity: 0` while the label still sits inside the
 * box, because the two would otherwise overlap; the label shrinks on focus or once
 * the field has a value. The examples were therefore shown only to someone who had
 * already started typing — i.e. to the one person who no longer needed them.
 */
const SHRUNK_LABEL = { inputLabel: { shrink: true } } as const;

interface FileContextFormProps {
  /** Current form state (controlled). */
  value: FileContextFormValue;
  /** Called with the full next form state on any field change. */
  onChange: (value: FileContextFormValue) => void;
  /** Disable all fields (e.g. while an upload is in flight). */
  disabled?: boolean;
}

/**
 * The per-file context-hint fields. Every hint is optional. One context per file
 * is the whole model (Maxine R4 ④ removed the batch-wide default form); the
 * Client is not part of this form — it is batch-wide with a hard consequence,
 * chosen once beside the file list (see ClientField).
 *
 * The fields are grouped by WHERE THE PIPELINE CONSUMES THEM, which is also the
 * honest way to state a limit the flat layout hid:
 * - "Where and when" (entity / period) are resolver fallbacks for rows that carry
 *   no hint of their own, applied to every file type. A blank entity hint is
 *   inferred server-side from the filename, sheet names and a row sample, and the
 *   period hint is read as a sentence by the period-context interpreter.
 * - "What to read" (unit / metrics / note) become lines in the extraction PROMPT,
 *   and only the messy-workbook Excel extractor is given them. Supplied for a PDF
 *   or a deck they are collected and then dropped, so the group says "spreadsheets
 *   only" rather than letting the screen imply they always apply.
 * @param {FileContextFormValue} value Current form state.
 * @param {(value: FileContextFormValue) => void} onChange Field-change handler.
 * @param {boolean | undefined} disabled Disable all fields.
 * @returns {JSX.Element}
 */
export default function FileContextForm({ value, onChange, disabled }: FileContextFormProps) {
  const updateField = (
    field: Exclude<keyof FileContextFormValue, 'metricHints'>,
    field_value: string
  ): void => {
    onChange({ ...value, [field]: field_value });
  };
  // The earlier files this one may replace. Only PROCESSED files qualify: the
  // amendment diffs against their records, and the API rejects anything else.
  const [replaceable, setReplaceable] = useState<Array<FileListItem>>([]);
  const [filesUnavailable, setFilesUnavailable] = useState<boolean>(false);
  useEffect(() => {
    let cancelled = false;
    fetchFiles()
      .then((files) => {
        if (!cancelled) {
          setReplaceable(files.filter((file) => file.status === 'PROCESSED'));
        }
      })
      .catch(() => {
        if (!cancelled) {
          setFilesUnavailable(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <Grid container spacing={2}>
      <HintGroup name="Where and when" what="fills the gaps in rows that do not say" />
      <Grid size={{ xs: 12, md: 6 }}>
        <TextField
          fullWidth
          size="small"
          label="Entity"
          placeholder={PLACEHOLDER.entity}
          // The single highest-value sentence on the screen: it is the only copy
          // that tells the uploader when NOT to do work. A blank hint is inferred.
          helperText="Leave blank and we read it from the file."
          value={value.entityHint}
          onChange={(event) => updateField('entityHint', event.target.value)}
          disabled={disabled}
          slotProps={SHRUNK_LABEL}
        />
      </Grid>
      <Grid size={{ xs: 12, md: 6 }}>
        <TextField
          fullWidth
          size="small"
          label="Period"
          placeholder={PLACEHOLDER.period}
          helperText="A sentence is fine. We work out the financial year."
          value={value.periodHint}
          onChange={(event) => updateField('periodHint', event.target.value)}
          disabled={disabled}
          slotProps={SHRUNK_LABEL}
        />
      </Grid>

      <HintGroup name="What to read" what="guides the AI (spreadsheets only)" divided />
      <Grid size={{ xs: 12, md: 6 }}>
        <TextField
          fullWidth
          size="small"
          label="Unit"
          placeholder={PLACEHOLDER.unit}
          helperText="Fills in units the sheet leaves out."
          value={value.unitHint}
          onChange={(event) => updateField('unitHint', event.target.value)}
          disabled={disabled}
          slotProps={SHRUNK_LABEL}
        />
      </Grid>
      <Grid size={{ xs: 12, md: 6 }}>
        <MetricHintsField
          value={value.metricHints}
          onChange={(next: Array<string>) => onChange({ ...value, metricHints: next })}
          placeholder={PLACEHOLDER.metrics}
          helperText="Pick from the metric list, or type your own."
          disabled={disabled}
        />
      </Grid>
      <Grid size={{ xs: 12 }}>
        <TextField
          fullWidth
          size="small"
          multiline
          minRows={2}
          label="Anything else"
          placeholder={PLACEHOLDER.note}
          helperText="Anything else that helps us read this file."
          value={value.contextNote}
          onChange={(event) => updateField('contextNote', event.target.value)}
          disabled={disabled}
          slotProps={SHRUNK_LABEL}
        />
      </Grid>
      {/* "Replaces" names ONE earlier file. It is the DECLARED amendment path — the
          only way an upload amends another since the silent duplicate inference was
          removed (Maxine 2026-08-19): a corrected re-upload that does not set it
          processes as its own file and hits the duplicate choice in review instead.
          The heading matches the review copy VERBATIM ("set 'Replaces an earlier
          file'"), so a reviewer sent here can find it (Maxine R5 ④). */}
      <HintGroup name="Replaces an earlier file" what="skips what has not changed" divided />
      <Grid size={{ xs: 12, sm: 8 }}>
        <TextField
          select
          fullWidth
          size="small"
          label="Replaces"
          value={value.supersedesFileId}
          onChange={(event) => updateField('supersedesFileId', event.target.value)}
          disabled={disabled || filesUnavailable}
          helperText={
            filesUnavailable
              ? 'Earlier files unavailable — this uploads as a new file.'
              : 'The best route for a corrected re-upload: only changed and new rows come back for review. Lists only files you uploaded yourself.'
          }
          slotProps={SHRUNK_LABEL}
        >
          <MenuItem value="">This is a new file</MenuItem>
          {replaceable.map((file) => (
            <MenuItem key={file.id} value={file.id}>
              {`${file.filename} · ${new Date(file.createdAt).toLocaleDateString()}`}
            </MenuItem>
          ))}
        </TextField>
      </Grid>
    </Grid>
  );
}
